import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Footer, Navbar } from "../components";
import { getMoviesByGenre, getTvShowsByGenre } from "../constants/api";
import { Play, Star, Film, Tv } from "lucide-react";
import MovieCardSkeleton from "../components/MovieCardSkeleton";

const GenreDetails = () => {
  const { type, id, name } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setPage(1);
      setHasMore(true);
      try {
        const data =
          type === "movies"
            ? await getMoviesByGenre(id, 1)
            : await getTvShowsByGenre(id, 1);
        setItems(data?.results || []);
        setHasMore(data?.page < data?.total_pages);
      } catch (error) {
        console.error("Error fetching genre items:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItems();
    }
  }, [id, type]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const data =
        type === "movies"
          ? await getMoviesByGenre(id, nextPage)
          : await getTvShowsByGenre(id, nextPage);
      
      setItems((prev) => [...prev, ...(data?.results || [])]);
      setPage(nextPage);
      setHasMore(data?.page < data?.total_pages);
    } catch (error) {
      console.error("Error loading more items:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const decodedName = decodeURIComponent(name || "");

  return (
    <div className="bg-black min-h-screen">
      <Navbar />

      <div className="bg-black padding pt-[4rem]">
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-1 w-12 bg-red"></div>
            <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
              {type === "movies" ? "Movies" : "TV Shows"}
            </span>
          </div>
          <h2 className="text-white mont font-bold lg:text-[42px] md:text-[36px] text-[28px] mb-1">
            {decodedName}
          </h2>
          <p className="text-white/60 mont text-[15px]">
            {type === "movies" ? "Movies" : "TV Shows"} in this genre
          </p>
        </div>

        {/* Items Grid */}
        <div>
          <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
            {loading ? (
              Array.from({ length: 12 }).map((_, index) => (
                <MovieCardSkeleton key={index} />
              ))
            ) : items.length > 0 ? (
              <>
                {items
                  .filter((item) => item.poster_path)
                  .map((item) => (
                    <Link
                      key={item.id}
                      to={
                        type === "movies"
                          ? `/movies/${item.id}`
                          : `/tvshows/${item.id}`
                      }
                      className="group modern-movie-card"
                    >
                      <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                        {/* Poster */}
                        <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                          <img
                            src={`https://image.tmdb.org/t/p/w500${item.poster_path}`}
                            alt={item.title || item.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextElementSibling.style.display = "flex";
                            }}
                          />
                          {/* Placeholder when image fails */}
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4"
                            style={{ display: "none" }}
                          >
                            {type === "movies" ? (
                              <Film className="text-white/40 mb-2" size={40} />
                            ) : (
                              <Tv className="text-white/40 mb-2" size={40} />
                            )}
                            <p className="text-white/60 mont text-xs text-center line-clamp-2">
                              {item.title || item.name}
                            </p>
                          </div>

                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                          {/* Play Button Overlay */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                              <Play className="text-white" size={28} fill="white" />
                            </div>
                          </div>

                          {/* Rating Badge */}
                          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                            <Star
                              className="text-yellow-400"
                              size={12}
                              fill="#FBBF24"
                            />
                            <span className="text-white mont text-xs font-semibold">
                              {item.vote_average?.toFixed(1)}
                            </span>
                          </div>
                        </div>

                        {/* Info */}
                        <div className="p-3">
                          <h3 className="text-white mont font-semibold text-[14px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                            {item.title || item.name}
                          </h3>
                          {(item.release_date || item.first_air_date) && (
                            <p className="text-white/50 mont text-xs mt-1">
                              {new Date(
                                item.release_date || item.first_air_date
                              ).getFullYear()}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}

                {/* Loading More Skeletons */}
                {loadingMore &&
                  Array.from({ length: 6 }).map((_, index) => (
                    <MovieCardSkeleton key={`skeleton-${index}`} />
                  ))}
              </>
            ) : (
              <div className="col-span-full text-center py-16">
                <p className="text-white/60 mont text-lg">
                  No {type === "movies" ? "movies" : "TV shows"} found for this genre.
                </p>
              </div>
            )}
          </div>

          {/* Load More Button */}
          {hasMore && items.length > 0 && (
            <div className="flex justify-center mt-12">
              <button
                type="button"
                className="group relative overflow-hidden px-8 py-3 bg-red text-white mont font-semibold text-lg rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/30"
                onClick={loadMore}
                disabled={loadingMore}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More
                      <Play className="ml-1" size={18} fill="white" />
                    </>
                  )}
                </span>
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GenreDetails;
